import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListScaffold } from "@/components/layout/ListScaffold";
import {
  Badge,
  Input,
  PageHeader,
  Select,
  Table,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
  VerdictBadge,
} from "@/components/ui";
import { useApi } from "@/lib/api";
import { ago, count, decimal } from "@/lib/format";
import { useDebounced } from "./useDebounced";

type Verdict = "" | "confirmed" | "likely" | "disputed" | "unknown" | "none";

export function CafesScreen() {
  const api = useApi();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [verdict, setVerdict] = useState<Verdict>("");
  const [cityId, setCityId] = useState("");
  const search = useDebounced(q, 250);

  const cities = useQuery({
    queryKey: ["admin", "cities"],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/cities");
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60_000,
  });

  const cafes = useQuery({
    queryKey: ["admin", "cafes", { search, verdict, cityId }],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/cafes", {
        params: {
          query: {
            q: search || undefined,
            verdict: verdict || undefined,
            cityId: cityId || undefined,
            limit: 50,
          },
        },
      });
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Corpus"
        title="Cafes"
        description="Most of these came from OpenStreetMap and nobody has said anything about them yet. Filter to “no verdict” to see the backlog."
      />
      <ListScaffold
        filters={
          <>
            <Input
              placeholder="Search name, street or city"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-[260px]"
            />
            <Select
              value={verdict}
              onChange={(e) => setVerdict(e.target.value as Verdict)}
            >
              <option value="">Any verdict</option>
              <option value="confirmed">Confirmed</option>
              <option value="likely">We think · nobody confirmed</option>
              <option value="disputed">Disputed</option>
              <option value="unknown">Unknown</option>
              <option value="none">No verdict yet</option>
            </Select>
            <Select value={cityId} onChange={(e) => setCityId(e.target.value)}>
              <option value="">All cities</option>
              {cities.data?.items.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </Select>
          </>
        }
        isLoading={cafes.isLoading}
        isError={cafes.isError}
        onRetry={() => void cafes.refetch()}
        isEmpty={(cafes.data?.items.length ?? 0) === 0}
        emptyTitle="No cafes match that"
        emptyHint="The corpus is large; a shorter search usually finds it."
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>Cafe</TH>
                <TH>City</TH>
                <TH>Matcha</TH>
                <TH align="right">Rating</TH>
                <TH align="right">Reviews</TH>
                <TH align="right">Photos</TH>
                <TH>State</TH>
                <TH>Updated</TH>
              </TR>
            </THead>
            <tbody>
              {cafes.data?.items.map((cafe) => (
                <TR key={cafe.id} onClick={() => navigate(`/cafes/${cafe.id}`)}>
                  <TD>
                    <span className="font-medium text-ink">{cafe.name}</span>
                    {cafe.street ? (
                      <span className="text-muted"> · {cafe.street}</span>
                    ) : null}
                  </TD>
                  <TD muted>{cafe.city ?? "—"}</TD>
                  <TD>
                    <VerdictBadge verdict={cafe.matchaVerdict} />
                  </TD>
                  <TD align="right" mono>
                    {cafe.matchaValue === null ? (
                      <span className="text-muted-2">—</span>
                    ) : (
                      <>
                        {decimal(cafe.matchaValue)}
                        <span className="text-muted-2">
                          {" "}
                          ({count(cafe.matchaCount)})
                        </span>
                      </>
                    )}
                  </TD>
                  <TD align="right" mono>
                    {count(cafe.reviewCount)}
                  </TD>
                  <TD align="right" mono>
                    {count(cafe.photoCount)}
                  </TD>
                  <TD>
                    {cafe.status === "open" ? (
                      <span className="text-muted">open</span>
                    ) : (
                      <Badge tone="danger">{cafe.status}</Badge>
                    )}
                  </TD>
                  <TD muted>{ago(cafe.updatedAt)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ListScaffold>
    </div>
  );
}
